<template>
  <div class="PayInFull">
    <p class="Tips">
      <span v-if="subscriptionDetails.contentProductName === 'Lumea' && currentCountryCode === 'GB'">
        {{ $t('subscription_details.accordion.payInFull.descriptionOneUK', 'By paying the remaining balance, your subscription will end and any monthly payments left will be automatically stopped.') }}
      </span>
      <span v-else>
      {{ $t('subscription_details.accordion.payInFull.descriptionOne', 'Are you happy with your') }}
      {{ subscriptionRatePlan.name }}?
      {{ $t('subscription_details.accordion.payInFull.descriptionTwo', 'You can make it all yours! Pay the remaining cost, keep the device and no further amount is due') }}
      </span>
    </p>

    <app-loading
      class="translucence"
      v-if="isPreviewing"
      component="looping-rhombuses-spinner"
      :animation-duration="2000"
      :size="30"
    />

    <discount-box
      v-if="subscriptionRatePlan.acceptPayInFullPromotion"
      :title-tips="$t('common.discountBox.title.tips', '(optional)')"
      @apply-promotion-code="applyPromotionCodeTask.run($event)"
      :is-applying="applyPromotionCodeTask.isActive"
      :applied-result="appliedPromotionResult"
    />

    <div class="PriceShow">
      <div class="PriceBox">
        <h4 class="title">
          {{ $t('subscription_details.accordion.payInFull.price.title.one', 'Buy your') }}
          {{ subscriptionRatePlan.name }}
          {{ $t('subscription_details.accordion.payInFull.price.title.two', 'for') }}
        </h4>

        <p class="amount">
          <span class="origin" :class="{ 'has-discount': previewResult.discount }">
            {{ subscriptionRatePlan.totalPurchaseValue | formatCurrency(true) }}
          </span>

          <span class="discount" v-if="previewResult.discount">
            {{ previewResult.originalAmount | formatCurrency(true) }}
          </span>
        </p>
      </div>
    </div>

    <div class="PriceCalc" data-name="PriceCalc">
      <h5 class="price">
        {{ $t('subscription_details.accordion.payInFull.priceCalculation', 'Price Calculation') }}
      </h5>

      <div class="PriceCalc_formula">
        <div class="priceItem-wrapper">
          <p class="priceItem flex-between">
            <span class="name">
              {{ subscriptionRatePlan.name }}
            </span>

            <span class="cash">
              {{ subscriptionRatePlan.totalPurchaseValue | formatCurrency }}
            </span>
          </p>

          <!--remove  invoicedPurchaseValue-->
          <!-- <p class="priceItem flex-between">
            <span class="name">
              {{ $t('subscription_details.accordion.payInFull.payed', '100% refund of what you payed already') }}
            </span>

            <span class="cash">
              - {{ subscriptionRatePlan.invoicedPurchaseValue | formatCurrency }}
            </span>
          </p> -->

          <p class="priceItem flex-between priceItem-discount" v-if="previewResult.discount">
            <span class="name">
              {{ $t('subscription_details.accordion.payInFull.totalDiscount', 'Total discount') }}
            </span>

            <span class="cash">
              - {{ previewResult.discount.amount | formatCurrency }}
            </span>
          </p>

          <!-- open balance -->
          <p class="priceItem flex-between" v-if="subscriptionRatePlan.balance">
            <span class="name">
              {{ $t('subscription_details.accordion.payInFull.openBalance', 'Open balance') }}
            </span>

            <span class="cash">
              {{ subscriptionRatePlan.balance | formatCurrency }}
            </span>
          </p>
        </div>

        <p class="price-final flex-between">
          <span class="name">
            {{ $t('subscription_details.accordion.payInFull.pay', 'Amount to pay') }}
          </span>

          <span class="cash" v-if="previewResult.discountedAmount !== undefined && previewResult.discountedAmount !== null">
            {{ previewResult.discountedAmount | formatCurrency(true) }}
          </span>

          <span class="cash" v-else>
            {{ subscriptionRatePlan.purchaseValueToPay | formatCurrency(true) }}
          </span>
        </p>
      </div>
    </div>

    <app-button
      class="button-orange"
      @click="redirect"
      :disabled="!subscriptionRatePlan.ableToPayInFull || !ableToSubscribe"
      data-name="redirect-purchase-checkout"
      :class="{'grey-color': paymentActivity.hasPaymentInProgress}"
      @mouseover="mouseover"
      @mouseleave="mouseleave"
    >
      {{ $t('subscription_details.accordion.payInFull.button.pay', 'pay in full now') }} ›
    </app-button>
    <v-popover
      offset="15"
      trigger="manual"
      placement="bottom-right"
      :open="isOpen"
      popover-wrapper-class="paymentDetail_poptip"
    >
      <template slot="popover">
        <div>{{ $t('common.paymentInprogress.toolTips.text', 'This option is disabled as we are processing your latest payment') }}</div>
      </template>
    </v-popover>
  </div>
</template>

<script>
  import Vue from 'vue'
  import { extractErrorMessage } from '@/utilities/common'
  import DiscountBox from 'components/DiscountBox'
  import { mapState } from 'vuex'
  import { detectedCulture } from '@/utilities/common/culture'

  export default {
    name: 'try-buy-pay-in-full',

    components: {
      DiscountBox
    },

    props: {
      subscriptionNumber: {
        type: String,
        required: true
      },

      subscriptionRatePlan: {
        type: Object,
        required: true
      },
    },

    data () {
      return {
        appliedPromotionResult: {},
        isPreviewing: true,
        previewResult: {},
        isOpen: false,
        currentCountryCode: detectedCulture.countryCode
      }
    },

    computed: {
      ...mapState('subscription', ['subscriptionDetails']),

      ...mapState('account', {
        ableToSubscribe: state => state.profile.personalDetail.ableToSubscribe,
      }),
      
      promotionCode () {
        return this.appliedPromotionResult.promotion && this.appliedPromotionResult.promotion.code
      },

      ...mapState('account', {
        paymentActivity: state => state.paymentActivity
      })
    },

    created () {
      if (this.subscriptionRatePlan.ableToPayInFull) {
          Vue.api.subscription.payInFullPreview(this.subscriptionNumber, this.subscriptionRatePlan.id).then(({ discount, originalAmount, discountedAmount }) => {
            this.previewResult = {
              discount,
              originalAmount,
              discountedAmount
            }
            this.$bus.$emit('unPaidCharge-refreshed', discountedAmount)
            this.isPreviewing = false
          })
      } else {
        this.isPreviewing = false
      }
    },

    methods: {
      redirect () {
        if (this.paymentActivity.hasPaymentInProgress) {
          return false
        } else {
          this.trackConversion({ name: 'interaction', description: 'buy_now' })
          if (this.promotionCode) {
            this.$router.push({ name: 'subscription-purchase', params: { id: this.subscriptionNumber }, query: { coupon: this.promotionCode } })
          } else {
            this.$router.push({ name: 'subscription-purchase', params: { id: this.subscriptionNumber } })
          }
        }
      },

      mouseleave () {
        if (this.paymentActivity.hasPaymentInProgress) {
          this.isOpen = false
        }
      },
      mouseover () {
        if (this.paymentActivity.hasPaymentInProgress) {
          this.isOpen = true
        }
      }
    },

    tasks (t) {
      return {
        applyPromotionCodeTask: t(async function (promotionCode) {
          this.appliedPromotionResult = {}
          try {
            const { discount, originalAmount, discountedAmount } = await Vue.api.subscription.payInFullPreview(this.subscriptionNumber, this.subscriptionRatePlan.id, promotionCode)
            this.previewResult = {
              discount,
              originalAmount,
              discountedAmount
            }
            this.appliedPromotionResult = {
              type: 'success',
              message: this.$t('common.discountBox.applyInfo.valid', 'Voucher code correct. Discount applied'),
              promotion: {
                code: discount.code,
                description: discount.description
              }
            }
          } catch (error) {
            this.appliedPromotionResult = {
              type: 'error',
              message: extractErrorMessage(error),
              promotion: null
            }
          }
        }).flow('restart')
      }
    }
  }
</script>

<style lang="scss" >
  .PayInFull {
    position: relative;

    .Tips {
      font-weight: 700;
      padding: 0 .06rem;

      @include media(tablet) {
        padding: 0;
      }
    }

    .PriceShow {
      margin-top: .1rem;
      margin-bottom: .26rem;

      .has-discount {
        position: relative;
        font-size: .17rem;
        font-weight: 700;
        line-height: .22rem;
        margin-right: .025rem;

        &::before {
          content: "";
          position: absolute;
          border-top: .01rem solid $blue;
          top: 50%;
          width: 95%;
          left: 2.5%;
          transform: rotate(-10deg);
        }
      }

      @include media(tablet) {
        margin-bottom: .3rem;
      }
    }

    .PriceCalc {
      margin-bottom: .2rem;

      > h5 {
        font-size: .17rem;
        color: $blue;
        font-weight: 800;
        margin-bottom: .08rem;
        padding: 0 .14rem;

        @include media(tablet) {
          margin-bottom: .12rem;
        }
      }

      .name,
      .cash {
        font-size: .14rem;
        font-weight: 500;
        color: #252525;
        flex: 1;
      }

      .cash {
        display: inline-flex;
        justify-content: flex-end;
      }

      &_formula {
        background: $white;
        border: .02rem solid $blue;
        margin-bottom: -.02rem;

        .priceItem {
          margin-bottom: .13rem;
          line-height: .18rem;

          &:nth-last-of-type(1) {
            margin-bottom: 0;
          }

          &-wrapper {
            padding: .17rem .13rem;
          }
        }

        @include media(tablet) {
          .priceItem {
            line-height: .18rem;
          }
        }

        .priceItem.priceItem-discount {
          color: $blue;

          .name,
          .cash {
            color: $blue;
          }
        }
      }

      &-wrapper {
        padding: .17rem .13rem;
      }

      .price-final {
        background: $green;
        color: $white;
        font-size: .14rem;
        font-weight: 500;
        line-height: .2rem;
        padding: .09rem .13rem;

        .cash,
        .name {
          color: $white;
        }

        .cash {
          font-size: .16rem;
        }
      }

      .AppButton {
        justify-content: space-between;
      }
    }

    .DiscountBox {
      margin-bottom: .26rem;

      &_title {
        padding: 0 .13rem;
      }

      &_form {
        .input-discount {
          .Form_control {
            border: .02rem solid $blue;
            padding: .04rem .1rem;
          }
        }
      }

      .coupon-apply-info {
        font-weight: 500;
        padding: 0 .13rem;
      }
    }

    .grey-color {
      background: #d8d8d8 !important;
      cursor: default;
    }
  }
</style>
